import { useEffect, useRef } from 'react'
import { SCENARIO_NUMBER_MAX_ABS, parseScenarioNumber } from './scenarioNumbers'
import { isBlankDate, toIsoDate } from './scenarioDates'

// Per-field undo/redo for the value boxes (letter 122 item 2) -- README.md ("Undo and redo in a field").
// The browser's own history is lost whenever React rewrites the input (blur reformat, focus swap), so
// each box keeps its own: a list of the texts it showed, pure functions here, the wiring in the hook.

// Consecutive typing (or deleting) within this window is one undo step, as the browser groups it.
export const COALESCE_MS = 1000

export function createHistory(text) {
  return { past: [], present: text, future: [], lastKind: null, lastAt: 0 }
}

function editKind(inputType) {
  if (inputType === 'insertText') return 'insert'
  if (typeof inputType === 'string' && inputType.startsWith('delete')) return 'delete'
  return null
}

export function recordEdit(history, text, inputType, now) {
  if (text === history.present) return history
  const kind = editKind(inputType)
  const coalesce = kind !== null && kind === history.lastKind && now - history.lastAt <= COALESCE_MS
  return {
    past: coalesce ? history.past : [...history.past, history.present],
    present: text,
    future: [],
    lastKind: kind,
    lastAt: now,
  }
}

export function undo(history) {
  if (history.past.length === 0) return history
  return { past: history.past.slice(0, -1), present: history.past[history.past.length - 1], future: [history.present, ...history.future], lastKind: null, lastAt: 0 }
}

export function redo(history) {
  if (history.future.length === 0) return history
  return { past: [...history.past, history.present], present: history.future[0], future: history.future.slice(1), lastKind: null, lastAt: 0 }
}

// Ctrl/Cmd+Z undoes; Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y redo (Cmd+Y: Cong, 147). `code` is the fallback only for a non-ASCII key
// (Ukrainian 'я'), so Dvorak's Ctrl+; on the physical Z stays what it is. Nothing during IME composition.
export function historyAction(e) {
  if (e.isComposing || e.nativeEvent?.isComposing || e.keyCode === 229) return null
  if (!(e.ctrlKey || e.metaKey) || e.altKey) return null
  const key = typeof e.key === 'string' ? e.key.toLowerCase() : ''
  const nonAscii = key.length === 1 && key.charCodeAt(0) > 127
  const isKey = (letter) => key === letter || (nonAscii && e.code === `Key${letter.toUpperCase()}`)
  if (isKey('z')) return e.shiftKey ? 'redo' : 'undo'
  if (isKey('y') && !(e.ctrlKey && e.metaKey) && !e.shiftKey) return 'redo'
  return null
}

// Two money/percent texts that store the same figure: "12." and "12", "$1,200" and "1200".
function figure(text) {
  const bare = String(text ?? '').replace(/[$,%\s]/g, '')
  if (bare === '') return ''
  const n = Number(bare)
  return Number.isFinite(n) ? String(n) : bare
}
// `max`: the box's own upper bound (Timeline 600). The parent holds a typed figure at the backend's
// bounds (scenarioNumbers.js), so "700" stored and shown as 600 is still the history's present.
export function sameFigure(present, shown, max) {
  if (figure(present) === figure(shown)) return true
  const typed = parseScenarioNumber(present)
  const stored = parseScenarioNumber(shown)
  if (typed == null || stored == null) return false
  const upper = Math.min(max ?? Infinity, SCENARIO_NUMBER_MAX_ABS)
  return Math.max(-SCENARIO_NUMBER_MAX_ABS, Math.min(upper, typed)) === stored
}

// Two date texts for the same day ("12/05/2024" and the committed "12/5/24"); unparseable ones as text.
const dateKey = (text) => (isBlankDate(text) ? '' : toIsoDate(text) ?? `text:${text}`)
export const sameDate = (a, b) => dateKey(a) === dateKey(b)
const sameText = (a, b) => a === b

// `apply(text)` puts a restored text on screen through the box's own change path. `same` decides
// whether what the box shows now is still the history's present; if not, the value was replaced from
// outside (load, import, a %/$ seed) and the history starts over from it.
export function useFieldHistory(inputRef, apply, same = sameText) {
  const historyRef = useRef(createHistory(null))
  const applyRef = useRef(apply)
  applyRef.current = apply
  const sameRef = useRef(same)
  sameRef.current = same

  const sync = (text) => {
    const history = historyRef.current
    historyRef.current = history.present !== null && sameRef.current(history.present, text) ? { ...history, lastKind: null } : createHistory(text)
  }

  const step = (action, currentText) => {
    sync(currentText)
    const before = historyRef.current
    const after = action === 'undo' ? undo(before) : redo(before)
    if (after === before) return
    historyRef.current = after
    applyRef.current(after.present)
  }

  // The Edit menu and the context menu reach the input as a history beforeinput, not a key.
  useEffect(() => {
    const el = inputRef.current
    if (!el) return undefined
    const onBeforeInput = (e) => {
      const action = e.inputType === 'historyUndo' ? 'undo' : e.inputType === 'historyRedo' ? 'redo' : null
      if (!action || !e.cancelable) return
      e.preventDefault()
      step(action, el.value)
    }
    el.addEventListener('beforeinput', onBeforeInput)
    return () => el.removeEventListener('beforeinput', onBeforeInput)
    // `step` reaches `apply` and `same` through refs, so the first render's listener stays current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    onFocus: (text) => sync(text),
    // `previous`: the text on screen before this edit, so a value replaced from outside mid-focus is
    // the step this edit undoes to, not a stale one.
    onEdit: (e, previous) => {
      const history = historyRef.current
      if (history.present === null || !sameRef.current(history.present, previous)) sync(previous)
      historyRef.current = recordEdit(historyRef.current, e.target.value, e.nativeEvent?.inputType, Date.now())
    },
    // Always swallowed, history or not: Chrome's page-wide undo would otherwise edit another field.
    onKeyDown: (e) => {
      const action = historyAction(e)
      if (!action) return false
      e.preventDefault()
      step(action, e.target.value)
      return true
    },
  }
}
